import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Container,
  Switch,
  Avatar
} from "@mui/material";
import {
  Plus,
  Mail,
  Edit,
  Trash2,
  LayoutGrid,
  List,
  Search
} from 'lucide-react';
import { tokens } from "../../theme";
import Modal from "../../utils/components/Modal";
import Cancellation from "../../utils/components/Cancellation";
import UserForm from "./UserForm";
import { enableOrDisableUser, getAllUsers, deleteUser, updateUser } from "./UserService";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import CustomDataTable from "../../utils/components/CustomDataTable";

const Users = () => {
  const [user, setUser] = useState(null);
  const setIsLoading = useLoadingSpinner();
  const [updateUsersData, setUpdateUsersData] = useState(false);
  const [params, setParams] = useState({ pageSize: 12, page: 0 });
  const [usersData, setUsersData] = useState({ content: [], totalElements: 0 });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEnableModalOpen, setIsEnableModalOpen] = useState(false);
  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [apiError, setApiError] = useState(null);

  // High-Volume Optimization States
  const [viewMode, setViewMode] = useState('card');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const lunchEditionModal = (u) => {
    setUser(u);
    setIsEditModalOpen(true);
  };

  const lunchEnableModal = (u) => {
    setUser({ ...u });
    setIsEnableModalOpen(true);
  };

  const lunchDeletionModal = (u) => {
    setUser(u);
    setIsDeletionModalOpen(true);
  };

  const lunchCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    await deleteUser(user.id, setUpdateUsersData, setIsLoading, setApiError);
    !apiError && setIsDeletionModalOpen(false);
  };

  const handleEnable = async () => {
    setIsLoading(true);
    await enableOrDisableUser(user.userId, !user.enabled, setUpdateUsersData, setIsLoading, setApiError);
    !apiError && setIsEnableModalOpen(false);
  };

  useEffect(() => {
    setIsLoading(true);
    getAllUsers(setUsersData, setIsLoading, params);
  }, [updateUsersData, params, setIsLoading]);

  const filteredUsers = usersData.content.filter(u => {
    const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.username?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'ALL' || u.roles?.includes(roleFilter);
    return matchesSearch && matchesRole;
  });

  const columns = [
    {
      field: "name",
      headerName: "User",
      width: 250,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap="12px">
          <Avatar sx={{ bgcolor: colors.blueAccent[500], width: 32, height: 32, fontSize: '14px' }}>
            {params.row.firstName?.charAt(0)}{params.row.lastName?.charAt(0)}
          </Avatar>
          <Box>
            <Typography fontWeight="bold" color={colors.grey[100]}>
              {params.row.firstName} {params.row.lastName}
            </Typography>
            <Typography variant="caption" color={colors.grey[400]}>
              @{params.row.username}
            </Typography>
          </Box>
        </Box>
      )
    },
    { field: "email", headerName: "Email", width: 220 },
    {
      field: "roles",
      headerName: "Roles",
      width: 180,
      renderCell: (params) => (
        <Box display="flex" gap="4px">
          {params.value?.map(role => (
            <Chip
              key={role}
              label={role}
              size="small"
              sx={{ bgcolor: `${colors.greenAccent[500]}1A`, color: colors.greenAccent[500], fontWeight: '700', fontSize: '10px' }}
            />
          ))}
        </Box>
      )
    },
    {
      field: "enabled",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={() => lunchEnableModal(params.row)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: colors.greenAccent[500] },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.greenAccent[500] }
          }}
        />
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Box display="flex" gap="4px">
          <IconButton size="small" onClick={() => lunchEditionModal(params.row)} sx={{ color: colors.grey[100] }}>
            <Edit size={18} />
          </IconButton>
          <IconButton size="small" onClick={() => lunchDeletionModal(params.row)} sx={{ color: colors.redAccent[500] }}>
            <Trash2 size={18} />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: "40px", pb: "80px" }}>
      {/* Enhanced Header Section */}
      <Box
        mb="40px"
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          padding: "32px",
          borderRadius: "24px",
          border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200]}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="24px">
          <Box>
            <Typography variant="h2" fontWeight="bold" sx={{ color: colors.grey[100], mb: "4px", fontFamily: "Josefin Sans" }}>
              User Management
            </Typography>
            <Typography variant="h5" color={theme.palette.mode === 'dark' ? colors.greenAccent[500] : colors.greenAccent[300]} fontWeight="500">
              {filteredUsers.length} system users identified
            </Typography>
          </Box>

          <Box display="flex" gap="12px" alignItems="center">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, next) => next && setViewMode(next)}
              size="small"
              sx={{
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                borderRadius: '12px',
                p: '4px',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '8px',
                  color: colors.grey[300],
                  '&.Mui-selected': {
                    bgcolor: colors.greenAccent[500],
                    color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                    '&:hover': { bgcolor: colors.greenAccent[400] }
                  }
                }
              }}
            >
              <ToggleButton value="card"><LayoutGrid size={18} /></ToggleButton>
              <ToggleButton value="list"><List size={18} /></ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<Plus size={20} />}
              onClick={lunchCreateModal}
              sx={{
                backgroundColor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                fontWeight: "bold",
                padding: "10px 24px",
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: `0 8px 20px ${colors.greenAccent[500]}33`,
                "&:hover": {
                  backgroundColor: colors.greenAccent[400],
                  transform: "translateY(-2px)"
                },
                transition: "all 0.2s"
              }}
            >
              Add User
            </Button>
          </Box>
        </Box>

        <Box display="flex" gap="16px" alignItems="center">
          <TextField
            placeholder="Search by name, email, or username..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                bgcolor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.2)" : "#FFFFFF",
                '& fieldset': { borderColor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200] }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color={colors.grey[300]} />
                </InputAdornment>
              ),
            }}
          />
          <Box display="flex" gap="8px">
            {['ALL', 'ADMIN', 'USER', 'MANAGER'].map((role) => (
              <Chip
                key={role}
                label={role}
                onClick={() => setRoleFilter(role)}
                sx={{
                  borderRadius: '10px',
                  fontWeight: '600',
                  bgcolor: roleFilter === role ? colors.blueAccent[500] : 'transparent',
                  color: roleFilter === role ? '#FFFFFF' : colors.grey[300],
                  border: `1px solid ${roleFilter === role ? colors.blueAccent[500] : colors.grey[400]}`,
                  '&:hover': { bgcolor: roleFilter === role ? colors.blueAccent[400] : 'rgba(255,255,255,0.05)' }
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Grid or List View */}
      {viewMode === 'card' ? (
        <Grid container spacing={3}>
          {filteredUsers.map((u) => (
            <Grid item xs={12} md={6} lg={4} key={u.id}>
              <Card sx={{
                borderRadius: "24px",
                backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#FFFFFF",
                border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200]}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                  borderColor: colors.greenAccent[500],
                }
              }}>
                <CardContent sx={{ p: "24px" }}>
                  <Box display="flex" justifyContent="space-between" mb="20px">
                    <Box display="flex" gap="4px">
                      {u.roles?.map(role => (
                        <Chip
                          key={role}
                          label={role}
                          size="small"
                          sx={{ bgcolor: `${colors.greenAccent[500]}1A`, color: colors.greenAccent[500], fontWeight: '700' }}
                        />
                      ))}
                    </Box>
                    <Switch
                      checked={u.enabled}
                      onChange={() => lunchEnableModal(u)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: colors.greenAccent[500] },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.greenAccent[500] }
                      }}
                    />
                  </Box>

                  <Box display="flex" alignItems="center" gap="16px" mb="24px">
                    <Avatar sx={{ bgcolor: colors.blueAccent[500], width: 56, height: 56, fontSize: '20px', fontWeight: 'bold' }}>
                      {u.firstName?.charAt(0)}{u.lastName?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}>
                        {u.firstName} {u.lastName}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[400]}>
                        @{u.username}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : colors.grey[100], borderRadius: '16px', p: '16px', mb: '24px' }}>
                    <Box display="flex" alignItems="center" gap="10px">
                      <Mail size={16} color={colors.grey[400]} />
                      <Typography variant="body2" color={colors.grey[200]} noWrap>
                        {u.email}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" gap="10px">
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<Edit size={18} />}
                      onClick={() => lunchEditionModal(u)}
                      sx={{
                        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : colors.grey[200],
                        color: colors.grey[100],
                        boxShadow: "none",
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:hover": { bgcolor: colors.primary[300] }
                      }}
                    >
                      Edit
                    </Button>
                    <Tooltip title="Delete User">
                      <IconButton
                        onClick={() => lunchDeletionModal(u)}
                        sx={{
                          bgcolor: `${colors.redAccent[500]}1A`,
                          color: colors.redAccent[500],
                          borderRadius: "12px",
                          p: "10px",
                          "&:hover": { bgcolor: colors.redAccent[500], color: "#FFFFFF" }
                        }}
                      >
                        <Trash2 size={20} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <CustomDataTable
          addRecordButtonTitle={"Add User"}
          headerColumn={columns}
          lunchCreateModal={lunchCreateModal}
          lunchDeletionModal={lunchDeletionModal} // This might need adjustment for bulk vs single
          paginationModel={params}
          rows={{ ...usersData, content: filteredUsers }}
          setPaginationModel={setParams}
          tableTitle={"Users List"}
        />
      )}

      {/* Modals */}
      <Modal isOpen={isEditModalOpen} onClose={setIsEditModalOpen}>
        <UserForm
          isUpdating={true}
          setState={setUpdateUsersData}
          toggleModal={setIsEditModalOpen}
          updateUser={updateUser}
          userToEdit={user}
        />
      </Modal>
      <Modal isOpen={isCreateModalOpen} onClose={setIsCreateModalOpen}>
        <UserForm
          isUpdating={false}
          setState={setUpdateUsersData}
          toggleModal={setIsCreateModalOpen}
        />
      </Modal>
      <Modal isOpen={isEnableModalOpen} onClose={setIsEnableModalOpen}>
        <Cancellation
          actionHandler={handleEnable}
          actionText={user?.enabled ? "Disable" : "Enable"}
          cancelText={"No, Keep"}
          setOpen={setIsEnableModalOpen}
          title={`${user?.enabled ? "Disable" : "Enable"} User`}
          warningMessage={`Are you sure you want to ${user?.enabled ? "disable" : "enable"} ${user?.firstName} ${user?.lastName}?`}
        />
      </Modal>
      <Modal isOpen={isDeletionModalOpen} onClose={setIsDeletionModalOpen}>
        <Cancellation
          actionHandler={handleDelete}
          actionText={"Delete"}
          cancelText={"Go Back"}
          setOpen={setIsDeletionModalOpen}
          title={`Delete User`}
          warningMessage={`Are you sure you want to delete ${user?.firstName} ${user?.lastName}? This action cannot be undone.`}
        />
      </Modal>
    </Container>
  );
};

export default Users;
